/**
 * Windermere Veneer Configuration
 *
 * Extended configuration for the Windermere theme veneer.
 * This includes feature flags, layout options, assets, and
 * content that is specific to Windermere Preparatory School.
 */

export const windermereVeneerConfig = {
  // Theme identification
  name: 'windermere',
  displayName: 'Windermere Preparatory School',
  parentBrand: 'Nord Anglia Education',

  // Feature flags - enable/disable Windermere-specific features
  features: {
    megaMenu: true,              // Full-width dropdown mega menu
    searchBar: true,             // Search bar in header
    scrollEffects: true,         // Header changes on scroll
    heroSection: true,           // Large hero section on landing
    quickActions: true,          // Quick action buttons on landing
    shopByGrade: true,           // Grade-level shopping dropdown
    shopByCollection: true,      // Collection-based shopping section
    contactInMenu: true,         // Contact info in navigation menu
    instagramLink: true,         // Link to Instagram
    promoBanner: true,           // Promotional banner at top
  },

  // Color palette - Windermere brand colors (Navy, Red, White)
  colors: {
    primary: '#0C2340',           // Windermere brand navy
    primaryLight: '#1a3a5c',      // Lighter navy
    primaryDark: '#07162a',       // Darker navy
    accent: '#BA0C2F',            // Windermere brand red (replaces teal)
    accentHover: '#8a0921',       // Darker red for hover
    // Primary action buttons - dark navy for white text contrast
    primaryActionBg: '#0C2340',   // Brand navy - ensures readable white text
    primaryActionBgHover: '#07162a', // Darker navy for hover
    headerBg: '#0C2340',          // Header background
    menuBg: '#0C2340',            // Dropdown menu background
    menuBorder: '#1a3a5c',        // Menu border color
    menuText: '#ffffff',          // Menu text color
    menuTextHover: '#BA0C2F',     // Menu text hover (red accent)
    ctaButton: '#BA0C2F',         // CTA button background (brand red)
    ctaButtonText: '#ffffff',     // CTA button text (white on red)
    ctaButtonHover: '#8a0921',    // CTA button hover (darker red)
  },

  // Asset paths - Windermere-specific images and icons
  assets: {
    logo: '/nord-anglia-school_windermere_logo_horizontal_white.png',
    heroBackground: '/images/campus-store.png',
    heroBackgroundAlt: '/images/campus-store-interior.jpg',
    favicon: '/images/windermere-favicon.ico',

    // Category/collection icons (SVG)
    icons: {
      shop: '/images/spirit-items.svg',
      peUniforms: '/images/pe-uniforms.svg',
      coldDayGear: '/images/cold-day-gear.svg',
      spiritItems: '/images/spirit-items.svg',
      newToSchool: '/images/new-to-school.svg',
      seasonal: '/images/seasonal-collection.svg',
    },

    // Student/lifestyle images
    lifestyle: {
      studentsEntering: '/images/group-of-students-entering-building.jpg',
      studentsEmbracing: '/images/group-of-happy-elementary-students-standing-embrac-2024-12-13-21-50-43-utc.jpg',
      studentSupplies: '/images/handsome-boy-child-putting-new-drawing-supplies-in-2025-05-15-12-29-02-utc.jpg',
    },
  },

  // Contact information
  contact: {
    email: 'campus.store@windermereprep.com',
    phone: '(407) 905-7737',
    phoneExtension: null,
    address: '6189 Winter Garden-Vineland Road, Windermere, FL 34786',
    instagram: 'https://instagram.com/windermereprep',
    website: 'https://www.windermereprep.com',
  },

  // Content/copy for various UI elements
  content: {
    heroTitle: 'WINDERMERE E-COMMERCE STORE',
    heroSubtitle: 'WELCOME TO',
    heroDescription: 'Windermere Preparatory School is the leading international prep school in Orlando and part of the worldwide Nord Anglia family of premium international schools. We are dedicated to supporting and inspiring your child to reach their greatest potential.',
    ctaPrimary: 'SHOP NOW',
    ctaSecondary: 'SIGN IN FOR FASTER CHECKOUT',
    trustBand: {
      secure: 'Secure Payments',
      fast: 'Fast Checkout',
      guest: 'Guest Checkout',
    },
    quickActionsTitle: 'Quick Actions',
    quickActionsSubtitle: 'Find what you need quickly with our convenient category shortcuts',
    collectionsTitle: 'Shop by Collection',
    collectionsSubtitle: 'Explore our curated collections and find exactly what you need',
    brandStrip: 'Part of the Nord Anglia Education family',
  },

  // Navigation structure for mega menu
  navigation: {
    shop: {
      title: 'Shop',
      // Real ecommerce categories by explicit id (/shop/category/<id>). ShopPage applies the filter
      // on deep-link even before its categories load (categories-watcher race guard). Seeded from
      // live WI categories; MJ to confirm/reorder. Replaces the old fragile slug links (pe/cold-day/spirit).
      items: [
        { label: 'All Products', path: '/shop' },
        { label: 'Uniforms & Apparel', path: '/shop/category/55' },
        { label: 'School Supplies', path: '/shop/category/25' },
        { label: 'Seasonal Collection', path: '/shop/category/86' },
        { label: 'New to School', path: '/shop/category/87' },
        { label: '1-Most Popular', path: '/shop/category/10' },
      ],
    },
    browse: {
      title: 'Browse by',
      items: [
        { label: 'Gender (Boys, Girls, Unisex)', action: 'filterGender' },
        { label: 'Grade (Elementary, Middle, High)', action: 'filterGrade' },
      ],
    },
    discover: {
      title: 'Discover more',
      items: [
        { label: 'Pick up Procedure for online orders', path: '/info/pickup' },
        { label: 'Store Location', path: '/info/location' },
        { label: 'Return Policy', path: '/info/returns' },
        { label: 'Uniform Policy', path: '/info/uniform-policy' },
      ],
    },
  },

  // Quick action buttons for landing page.
  // Curated to real ecommerce categories by explicit categoryId (deterministic /shop/category/<id>
  // navigation — no fuzzy slug matching). Seeded from live Windermere categories; MJ to confirm/reorder.
  quickActions: [
    { id: 'shop', label: 'Shop All', description: 'Browse all products', icon: 'shopping-bag', path: '/shop' },
    { id: 'popular', label: '1-Most Popular', description: 'Our most popular items', icon: 'heart', categoryId: 10 },
    { id: 'uniforms', label: 'Uniforms & Apparel', description: 'Uniforms & apparel', icon: 'user', categoryId: 55 },
    { id: 'supplies', label: 'School Supplies', description: 'Everything for class', icon: 'book', categoryId: 25 },
  ],

  // Grade levels for dropdown
  grades: [
    { value: 'pre-k', label: 'Pre-K' },
    { value: 'kindergarten', label: 'Kindergarten' },
    { value: '1st', label: '1st Grade' },
    { value: '2nd', label: '2nd Grade' },
    { value: '3rd', label: '3rd Grade' },
    { value: '4th', label: '4th Grade' },
    { value: '5th', label: '5th Grade' },
    { value: '6th', label: '6th Grade' },
    { value: '7th', label: '7th Grade' },
    { value: '8th', label: '8th Grade' },
    { value: '9th', label: '9th Grade' },
    { value: '10th', label: '10th Grade' },
    { value: '11th', label: '11th Grade' },
    { value: '12th', label: '12th Grade' },
  ],

  // Collections for "Shop by Collection" section.
  // Real ecommerce categories by explicit categoryId (the remaining three; Most Popular / Uniforms /
  // Supplies live in quickActions above). 'grade' = the Shop-by-Grade dropdown; 'website' = external.
  collections: [
    { id: 'grade', label: 'Shop by Grade', description: null, type: 'dropdown', icon: 'book' },
    { id: 'new-to-school', label: 'New to School', description: 'Everything you need to start', icon: 'book-open', categoryId: 87 },
    { id: 'seasonal', label: 'Seasonal Collection', description: 'Current season essentials', icon: 'sun', categoryId: 86 },
    { id: 'blowout', label: 'Blow Out Sale', description: 'Clearance deals & markdowns', icon: 'bolt', categoryId: 18 },
    { id: 'website', label: 'School Website', description: 'Visit our main site', type: 'external', icon: 'external-link' },
  ],
}

export default windermereVeneerConfig
